<template>
  <v-flex xs12 lg6>
    <v-card>
      <v-card-title class="py-1">
        <v-flex>
          <h2 class="ml-1">{{title}}</h2>
        </v-flex>
        <v-flex class="mr-1" xs2 sm1>
          <v-menu lazy top offset-y offset-x content-class="students_ellipsis_menu" :disabled="chartsLoading">
            <v-btn :id="chartActionsId" :disabled="chartsLoading" class="students_ellipsis" flat icon slot="activator">
              <v-icon large>more_horiz</v-icon>
            </v-btn>
            <v-list>
              <v-list-tile :id="chartDeleteBtnId" v-if="!flagHideDeleteOption" @click="graphDeleteClicked(graph.id)">
                <v-icon class="pr-2">delete</v-icon>
                {{$t('dashboard.chart.delete_button')}}
              </v-list-tile>
              <v-list-tile :id="chartOrderBtnId" @click="showReorderDialog">
                <v-icon class="pr-2">reorder</v-icon>
                {{$t('dashboard.chart.reorder_button')}}
                <v-dialog v-model="reorderDialog" max-width="375">
                  <reorder-chart-modal :graphs="dashboardGraphs"></reorder-chart-modal>
                </v-dialog>
              </v-list-tile>
              <v-list-tile :id="chartFilterBtnId" @click="graphFilterClicked(graph.id)">
                <v-icon class="pr-2">filter_list</v-icon>
                {{$t('dashboard.chart.filter_button')}}
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-flex>

      </v-card-title>
      <v-progress-linear v-if="chartsLoading" height="2" :indeterminate="true" color="accent"></v-progress-linear>
      <v-card-media class="pa-2" ref="card" style="display: contents">
        <div style="min-width: 220px; width: 100%">
          <component
            :is="graphType"
            :graph="graph"
            :graph-data="chartData"
            :chart-updated="chartUpdated"
            :updated-id="updatedChartId">
            <div v-html="graphType"></div>
          </component>
        </div>
      </v-card-media>

      <v-flex>
        <p v-if="noData" class="text-xs-center" style="margin-bottom: 5px;">{{$t('dashboard.chart.no_data')}}</p>
        <v-icon v-else class="mr-1">mdi-blank</v-icon>
      </v-flex>
    </v-card>
  </v-flex>
</template>

<script>
  import DashboardLineGraph from './DashboardLineGraph'
  import DashboardBarGraph from './DashboardBarGraph'
  import DashboardPieGraph from './DashboardPieGraph'
  import DashboardDoughnutGraph from './DashboardDoughnutGraph'
  import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
  import {
    HIDE_CHART_FILTERS, SET_REORDER_CHART_DIALOG_VISIBILITY,
    SET_SELECTED_CHART_ID
  } from '../../store/mutation-types'
  import {
    DELETE_DASHBOARD_CHART,
    GET_SAVED_FILTERS_FOR_CHARTS
  } from '../../store/action-types'
  import { HAS_FEATURE_FLAG } from '../../store/getter-types'
  import ReorderChartModal from './ReorderChartModal'

  export default {
    name: 'DashboardGraphCard',
    components: {ReorderChartModal, DashboardLineGraph, DashboardBarGraph, DashboardPieGraph, DashboardDoughnutGraph},
    data () {
      return {
        chartData: {},
        title: '',
        noData: false
      }
    },
    props: {
      graph: {
        type: Object,
        required: true
      },
      graphKey: {
        type: Number,
        required: true
      }
    },
    computed: {
      ...mapState({
        dashboardGraphs: state => state.dashboard.dashboardGraphs,
        dashboardGraphsData: state => state.dashboard.dashboardGraphsData,
        chartUpdated: state => state.dashboard.chartUpdated,
        updatedChartId: state => state.dashboard.updatedChartId,
        chartsLoading: state => state.dashboard.chartsLoading,
        moduleInsights: state => state.profile.module.insights,
        functionChart: state => state.dashboard.functions.chart,
        optionDisableChartDelete: state => state.dashboard.options.disable.chartDelete
      }),
      ...mapGetters({
        hasFeatureFlag: HAS_FEATURE_FLAG
      }),
      graphTitle () {
        let key = `dashboard.${this.graph.chartTitle}`
        if (this.$i18n.keyExists(key)) {
          return this.$t(key)
        } else {
          return this.graph.chartTitle
        }
      },
      graphType () {
        return 'dashboard-' + this.graph.chartType.toString().toLowerCase() + '-graph'
      },
      getGraphData () {
        let obj = {}
        let data = this.dashboardGraphsData.filter(el => el.id === this.graph.id)[0]
        if (data) {
          obj = data
        }
        return obj
      },
      receivedAllGraphData () {
        return this.dashboardGraphs.length === this.dashboardGraphsData.length
      },
      flagHideDeleteOption () {
        return this.hasFeatureFlag(this.moduleInsights + '.' + this.functionChart + '.' + this.optionDisableChartDelete)
      },
      chartActionsId () {
        return 'chart-actions-' + this.graph.id
      },
      chartFilterBtnId () {
        return 'chart-filter-btn-' + this.graph.id
      },
      chartDeleteBtnId () {
        return 'chart-delete-btn-' + this.graph.id
      },
      chartOrderBtnId () {
        return 'chart-order-btn-' + this.graph.id
      },
      reorderDialog: {
        get () {
          return this.$store.state.dashboard.reorderDialogVisible
        },
        set (value) {
          this.$store.commit(SET_REORDER_CHART_DIALOG_VISIBILITY, value)
        }
      }
    },
    methods: {
      ...mapActions({
        myFilterChange: GET_SAVED_FILTERS_FOR_CHARTS,
        deleteChart: DELETE_DASHBOARD_CHART
      }),
      ...mapMutations({
        setSelectedChartId: SET_SELECTED_CHART_ID,
        hideFilters: HIDE_CHART_FILTERS
      }),
      graphFilterClicked (chartId) {
        this.hideFilters(this.graph.hideFilters)
        this.setSelectedChartId(chartId)
        this.myFilterChange(chartId)
      },
      graphDeleteClicked (chartId) {
        let payload = {
          id: chartId,
          toBeDeleted: true
        }
        this.deleteChart(payload)
      },
      setValues () {
        this.chartData = this.getGraphData
        this.title = this.graphTitle
        this.noData = !this.chartData.chartData
      },
      showReorderDialog () {
        this.reorderDialog = true
      }
    },
    mounted () {
      if (this.receivedAllGraphData) {
        // triggered every time page is hit
        // re-assign values
        this.setValues()
      }
    },
    watch: {
      chartUpdated (value) {
        if (value) {
          this.setValues()
        }
      }
    }
  }
</script>

<style scoped>
  .float-left {
    float: left;
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/DashboardGraphCard.vue